import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { NavLink as RouterLink } from 'react-router-dom';
import {
  Box,
  List,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  ListSubheader,
  Collapse,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ROUTE_PATH from '../../../config/routes.config';
import Iconify from '../../common/iconify.component';
import {
  CONTENT_ALIAS,
  FOCUS_PALETTE_BGCOLOR,
  SHOW_CONTENT_CATEGORIES,
  SHOW_CONTENT_SEQUENCE,
  SHOW_CONTENT_VIDEOS,
  SHOW_NOTIFICATIONS,
  SHOW_OVERVIEW_DASHBOARD,
  SHOW_REALTIME_DASHBOARD,
  SHOW_REPORTS_LIVE_USERS,
  SHOW_REPORTS_OVERVIEW_VIDEOS,
  SHOW_REPORTS_WATCH_SESSIONS,
  SHOW_SALUTATION,
  SHOW_SUBSCRIBERS,
  SHOW_GROUPS,
  SHOW_CONTENT_GATHERINGS,
  SHOW_REPORTS_LIVE_EVENT,
  SHOW_CONTENT_RESOURCES,
  SHOW_TESTIMONIALS,
  SHOW_RECORD_MESSAGE,
  SHOW_LEADING_TEACHERS,
  SHOW_ADMIN_USERS,
  SHOW_QUIZ,
  SHOW_DATA_CHANGE_LOG,
} from '../../../config/const.config';
import palette from '../../../theme/palette';
import useAccess from '../../../hooks/access.hook';
import { AccessContext } from '../../../context/access.context';

const StyledSubHeader = styled((props) => <ListSubheader {...props} />)(
  ({ theme }) => ({
    background: 'transparent',
    color: theme.palette.grey[200],
  })
);

const StyledNavItem = styled((props) => (
  <ListItemButton disableGutters {...props} />
))(({ theme }) => ({
  ...theme.typography.body2,
  height: 48,
  position: 'relative',
  textTransform: 'capitalize',
  color: theme.palette.text.secondary,
  borderRadius: theme.shape.borderRadius,
}));

const StyledNavItemIcon = styled(ListItemIcon)({
  width: 24,
  height: 24,
  color: palette.background.paper,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 24,
});

const NavItem = ({ title, path, icon, subNav }) => (
  <StyledNavItem
    component={RouterLink}
    to={path}
    sx={{
      '&.active': {
        color: palette.common.white,
        bgcolor: FOCUS_PALETTE_BGCOLOR,
        fontWeight: 'fontWeightBold',
      },
      height: subNav ? 36 : 48,
    }}
  >
    {!subNav && <StyledNavItemIcon>{icon}</StyledNavItemIcon>}
    <ListItemText
      disableTypography
      primary={title}
      sx={{
        color: palette.common.white,
        pl: subNav ? 6 : 0,
        overflowX: 'clip',
        textWrap: 'nowrap',
        textOverflow: 'ellipsis',
        paddingRight: 2,
      }}
    />
  </StyledNavItem>
);
NavItem.propTypes = {
  title: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  icon: PropTypes.element,
  subNav: PropTypes.bool,
};
NavItem.defaultProps = {
  icon: <div />,
  subNav: false,
};

const NavGroup = ({ title, routePath, icon, navGroup }) => {
  const urlParts = window.location.href.split('/');
  const routePathParts = routePath.split('/');

  const initOpen =
    urlParts[urlParts.length - 2] === routePathParts[routePathParts.length - 1];

  const [open, setOpen] = useState(initOpen);
  const handleClick = () => setOpen(!open);

  return (
    <>
      <StyledNavItem onClick={handleClick}>
        <StyledNavItemIcon>{icon}</StyledNavItemIcon>
        <ListItemText
          disableTypography
          primary={title}
          sx={{
            color: palette.common.white,
            overflowX: 'clip',
            textWrap: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        />
        <StyledNavItemIcon>
          <Iconify icon={open ? 'mdi:menu-up' : 'mdi:menu-down'} />
        </StyledNavItemIcon>
      </StyledNavItem>

      <Collapse in={open} timeout="auto">
        <List disablePadding sx={{ p: 1 }}>
          {navGroup.map(
            (nav) =>
              nav.status === 1 && (
                <NavItem
                  key={nav.title}
                  title={nav.title}
                  path={`${routePath}/${nav.slug}`}
                  icon={<div />}
                  subNav
                />
              )
          )}
        </List>
      </Collapse>
    </>
  );
};
NavGroup.propTypes = {
  title: PropTypes.string.isRequired,
  routePath: PropTypes.string.isRequired,
  icon: PropTypes.element.isRequired,
  navGroup: PropTypes.array.isRequired,
};

const NavItems = () => {
  const { hasAccess } = useAccess();
  const { accessData } = useContext(AccessContext);

  return (
    <Box>
      <List disablePadding sx={{ p: 1 }}>
        {SHOW_OVERVIEW_DASHBOARD &&
          !!hasAccess(accessData.role, 'dashboard') && (
            <NavItem
              title="Overview Dashboard"
              path={ROUTE_PATH.DASHBOARD}
              icon={<Iconify icon="ic:round-dashboard" />}
            />
          )}

        {SHOW_REALTIME_DASHBOARD &&
          !!hasAccess(accessData.role, 'dashboard') && (
            <NavItem
              title="Live Users (Per Platform)"
              path={ROUTE_PATH.REALTIME_DASHBOARD}
              icon={<Iconify icon="tabler:activity-heartbeat" />}
            />
          )}

        {SHOW_SUBSCRIBERS && !!hasAccess(accessData.role, 'subscribers') && (
          <NavItem
            title="Subscribers"
            path={ROUTE_PATH.SUBSCRIBERS}
            icon={<Iconify icon="ci:users" />}
          />
        )}

        {SHOW_GROUPS && !!hasAccess(accessData.role, 'community_groups') && (
          <NavItem
            title="Community Groups"
            path={ROUTE_PATH.GROUPS}
            icon={<Iconify icon="grommet-icons:group" />}
          />
        )}

        {SHOW_QUIZ && !!hasAccess(accessData.role, 'quiz') && (
          <NavItem
            title="Quiz Management"
            path={ROUTE_PATH.QUIZ}
            icon={<Iconify icon="hugeicons:quiz-03" />}
          />
        )}

        {SHOW_ADMIN_USERS && !!hasAccess(accessData.role, 'users') && (
          <NavItem
            title="Admin Users"
            path={ROUTE_PATH.ADMIN_USERS}
            icon={<Iconify icon="mdi:users-check" />}
          />
        )}

        <StyledSubHeader component="div">Content</StyledSubHeader>

        {SHOW_CONTENT_CATEGORIES &&
          !!hasAccess(accessData.role, 'program_courses') && (
            <NavItem
              title={`${CONTENT_ALIAS.CATEGORY_PLURAL}`}
              path={ROUTE_PATH.CONTENT_CATEGORIES}
              icon={<Iconify icon="hugeicons:course" />}
            />
          )}

        {SHOW_CONTENT_VIDEOS && !!hasAccess(accessData.role, 'videos') && (
          <NavItem
            title={`${CONTENT_ALIAS.VIDEO_PLURAL}`}
            path={ROUTE_PATH.CONTENT_VIDEOS}
            icon={<Iconify icon="octicon:video-16" />}
          />
        )}

        {SHOW_CONTENT_RESOURCES &&
          !!hasAccess(accessData.role, 'resources') && (
            <NavItem
              title={`${CONTENT_ALIAS.RESOURCES_PLURAL}`}
              path={ROUTE_PATH.CONTENT_RESOURCES}
              icon={<Iconify icon="iconamoon:category" />}
            />
          )}

        {SHOW_CONTENT_GATHERINGS &&
          !!hasAccess(accessData.role, 'community_gatherings_zoom') && (
            <NavItem
              title="Gatherings"
              path={ROUTE_PATH.CONTENT_GATHERINGS}
              icon={<Iconify icon="healthicons:community-meeting" />}
            />
          )}

        {SHOW_LEADING_TEACHERS &&
          !!hasAccess(accessData.role, 'leading_teachers') && (
            <NavItem
              title={`${CONTENT_ALIAS.TEACHER_PLURAL}`}
              path={ROUTE_PATH.CONTENT_TEACHERS}
              icon={<Iconify icon="hugeicons:teacher" />}
            />
          )}

        {SHOW_RECORD_MESSAGE &&
          (!!hasAccess(accessData.role, 'ms_voice_recorded_videos') ||
            !!hasAccess(accessData.role, 'ms_voice_recorded_audios')) && (
            <NavGroup
              title="MS Voice"
              routePath={ROUTE_PATH.CONTENT_RECORDED_MESSAGE}
              icon={<Iconify icon="octicon:video-16" />}
              navGroup={[
                {
                  title: 'Recorded Videos',
                  slug: 'videos',
                  status: 1,
                },
                {
                  title: 'Recorded Audios',
                  slug: 'audios',
                  status: 1,
                },
              ]}
            />
          )}

        {SHOW_CONTENT_SEQUENCE && !!hasAccess(accessData.role, 'sequence') && (
          <NavGroup
            title="Sequence"
            routePath={ROUTE_PATH.CONTENT_SEQUENCE}
            icon={<Iconify icon="fluent-mdl2:number-sequence" />}
            navGroup={[
              {
                title: `${CONTENT_ALIAS.CATEGORY_SINGULAR}`,
                slug: 'category',
                status: 1,
              },
            ]}
          />
        )}

        {SHOW_TESTIMONIALS &&
          (!!hasAccess(
            accessData.role,
            'testimonials_user_text_testimonials'
          ) ||
            !!hasAccess(
              accessData.role,
              'testimonials_user_video_testimonials'
            )) && (
            <NavGroup
              title="Testimonials"
              routePath={ROUTE_PATH.CONTENT_TESTIMONIALS}
              icon={<Iconify icon="fluent-mdl2:office-chat" />}
              navGroup={[
                {
                  title: 'User Text Testimonials',
                  slug: 'user-text-testimonials',
                  status: 1,
                },
                {
                  title: 'User Video Testimonials',
                  slug: 'user-video-testimonials',
                  status: 1,
                },
              ]}
            />
          )}

        {SHOW_NOTIFICATIONS &&
          !!hasAccess(accessData.role, 'notifications') && (
            <NavItem
              title="Notifications"
              path={ROUTE_PATH.NOTIFICATIONS}
              icon={
                <Iconify icon="material-symbols:notification-add-outline-rounded" />
              }
            />
          )}

        {SHOW_SALUTATION && !!hasAccess(accessData.role, 'salutation') && (
          <NavItem
            title="Salutation"
            path={ROUTE_PATH.SALUTATION}
            icon={<Iconify icon="mdi:human-greeting-variant" />}
          />
        )}

        {(SHOW_REPORTS_OVERVIEW_VIDEOS ||
          SHOW_REPORTS_LIVE_USERS ||
          SHOW_REPORTS_WATCH_SESSIONS) && (
          <StyledSubHeader component="div">Analytics Reports</StyledSubHeader>
        )}

        {SHOW_REPORTS_OVERVIEW_VIDEOS &&
          !!hasAccess(accessData.role, 'video_watch_overview') && (
            <NavItem
              title="Video Watch Overview"
              path={ROUTE_PATH.REPORTS_OVERVIEW_VIDEOS}
              icon={<Iconify icon="ic:outline-analytics" />}
            />
          )}

        {SHOW_REPORTS_WATCH_SESSIONS &&
          !!hasAccess(accessData.role, 'video_watch_sessions') && (
            <NavItem
              title="Video Watch Sessions"
              path={ROUTE_PATH.REPORTS_SESSIONS}
              icon={<Iconify icon="ic:outline-list-alt" />}
            />
          )}

        {SHOW_REPORTS_LIVE_USERS &&
          !!hasAccess(accessData.role, 'live_users') && (
            <NavItem
              title="Live Users"
              path={ROUTE_PATH.REPORTS_LIVE_USERS}
              icon={<Iconify icon="ic:round-live-tv" />}
            />
          )}

        {SHOW_REPORTS_LIVE_EVENT &&
          !!hasAccess(accessData.role, 'live_event_report') && (
            <NavItem
              title="Live Event Report"
              path={ROUTE_PATH.REPORTS_LIVE_EVENT}
              icon={<Iconify icon="ic:outline-analytics" />}
            />
          )}

        {SHOW_DATA_CHANGE_LOG && (
          <NavItem
            title="Data Changed Logs"
            path={ROUTE_PATH.DATA_CHANGE_LOGS}
            icon={<Iconify icon="ic:outline-analytics" />}
          />
        )}
      </List>
    </Box>
  );
};

NavItems.propTypes = {};

export default NavItems;
